import {addToast} from '@albert-io/atomic';
import errorCodes from 'client/errorCodes';
import {genericMandarkRequest} from 'resources/mandark.resource';
import notifier from '@albert-io/notifier';
import {query} from '@albert-io/json-api-framework/request/builder';
import sessionStore from 'client/Session/SessionStore';
import {useStudentHomepageStore} from '../useStudentHomepageStore';

interface EnrollPayload {
  onReject?: (err?: any) => void;
  onResolve?: () => void;
  relationship: any;
}

export default function useEnrollStudent() {
  const setExceededHardCapLimit = useStudentHomepageStore(
    (state) => state.setExceededHardCapLimit
  );
  const setIsStudentEnrollmentError = useStudentHomepageStore(
    (state) => state.setIsStudentEnrollmentError
  );
  const setIsStudentEnrollmentPending = useStudentHomepageStore(
    (state) => state.setIsStudentEnrollmentPending
  );
  const setStudentHasInvalidEmail = useStudentHomepageStore(
    (state) => state.setStudentHasInvalidEmail
  );

  function enrollStudent({
    onReject = () => {},
    onResolve = () => {},
    relationship
  }: EnrollPayload) {
    const studentId = sessionStore.getUserId();

    if (!studentId) {
      return;
    }

    setIsStudentEnrollmentPending(true);

    genericMandarkRequest(
      'post',
      query()
        .mandarkEndpoint(['students_v2', studentId, 'relationships', 'classrooms_v1'])
        .done(),
      {
        data: [relationship]
      }
    )
      .then(() => {
        addToast({
          duration: 10000,
          message: 'You have joined a new class',
          title: 'Success!',
          color: 'positive'
        });

        onResolve();
      })
      .catch((error) => {
        setIsStudentEnrollmentError(true);

        const exceededHardCapLimitError =
          error.response?.status === 403 &&
          error.response?.body.errors[0].code === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT;

        if (exceededHardCapLimitError) {
          setExceededHardCapLimit(true);
        }

        // don't notify for 400 errors (to suppress invalid join code)
        if (error.response?.status !== 400) {
          notifier.notify(error, {
            message: 'Failed to enroll student using relationship',
            tags: 'useEnrollStudent',
            context: {
              relationship: relationship
            }
          });
        }

        // Catch the Errors.BadRequest.InvalidEmailDomain here to cover join code, email link, and invitation card enrollment flows
        const invalidEmail =
          error.response?.status === 400 &&
          error.response?.body.errors[0].code ===
            errorCodes.BAD_REQUEST.VALIDATION_ERROR.INVALID_EMAIL_DOMAIN;

        if (invalidEmail) {
          setStudentHasInvalidEmail(true);
        }

        onReject(error);
      })
      .finally(() => {
        setIsStudentEnrollmentPending(false);
      });
  }

  return enrollStudent;
}
